import { memo, useCallback } from 'react';

import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger } from '@air/primitive-select';

import { UpsellFlashIconButton } from '~/components/SharePrivateContentModal/shared/UpsellFlashIconButton';
import { usePrivateContextSourcesList } from '~/components/v2/hooks/queries/usePrivateContextSourcesList';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

/**
 * Radix Select marks an item selected by matching a non-empty string `value`, so "no brand context
 * selected" cannot be the empty string. This module-scope sentinel stands in for the unselected
 * state and is mapped back to `undefined` before it ever reaches the parent.
 */
const BRAND_CONTEXT_NONE_VALUE = 'none';

export type ShareBrandContextRowProps = {
  /** data-testid for the row container. */
  'data-testid'?: string;
  disabled: boolean;
  /**
   * Plan-gate-only upsell affordance — kept separate from `disabled` (which also covers in-flight
   * saves) so the flash icon never flashes during saves.
   */
  showUpsell: boolean;
  /** Currently selected context source id, controlled by the parent (`undefined` => None). */
  selectedContextSourceId: string | undefined;
  /**
   * Reports the picked context source up to the parent (`undefined` when "None" is chosen). The
   * parent owns fetching the selected source's fields and running the brand-color auto-map; this row
   * stays presentational and only lists the available sources.
   */
  onSelectContextSource: (contextSourceId: string | undefined) => void;
};

/**
 * Design-tab row that lets the user pick a workspace brand kit (Context Source) to seed the share
 * link's design. Presentational: it fetches the LIST of sources for the dropdown options, but leaves
 * fetching the selected source's fields and the actual color mapping to the parent tab.
 */
export const ShareBrandContextRow = memo(
  ({
    'data-testid': dataTestId,
    disabled,
    showUpsell,
    selectedContextSourceId,
    onSelectContextSource,
  }: ShareBrandContextRowProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { data: listData } = usePrivateContextSourcesList(
      { workspaceId: currentWorkspace?.id },
      { enabled: !disabled },
    );
    const sources = listData?.data ?? [];

    const handleValueChange = useCallback(
      (value: string) => {
        onSelectContextSource(value === BRAND_CONTEXT_NONE_VALUE ? undefined : value);
      },
      [onSelectContextSource],
    );

    return (
      <div className="flex items-center gap-1.5" data-testid={dataTestId}>
        {/* `?? ''` keeps the Select CONTROLLED when no kit is selected (including Discard clearing
            the selection back to undefined) — '' renders the placeholder and never collides with an
            option value, while an undefined value would flip Radix to uncontrolled and warn. */}
        <Select disabled={disabled} onValueChange={handleValueChange} value={selectedContextSourceId ?? ''}>
          <SelectTrigger
            className="w-full"
            data-testid="SHARE_DESIGN_BRAND_CONTEXT_SELECT"
            disabled={disabled}
            // "None" (matching the explicit None option) rather than "Brand Kit": the placeholder is
            // what renders when nothing is selected, and a kit-like label made the empty state read
            // as a selected kit named "Brand Kit". Selecting None maps to undefined, which re-shows
            // this placeholder — so unselected and selected-None read identically, and truthfully.
            placeholder="None"
            size="medium"
          />
          <SelectContent>
            <SelectItem data-testid="SHARE_DESIGN_BRAND_CONTEXT_OPTION_NONE" value={BRAND_CONTEXT_NONE_VALUE}>
              None
            </SelectItem>
            {sources.length > 0 && <SelectSeparator />}
            {sources.map((source) => (
              <SelectItem data-testid="SHARE_DESIGN_BRAND_CONTEXT_OPTION" key={source.id} value={source.id}>
                {source.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showUpsell && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
      </div>
    );
  },
);

ShareBrandContextRow.displayName = 'ShareBrandContextRow';
